//libs
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
//helpers
import {
  getCollectionPageSchema,
  getItemListSchema,
  getBreadcrumbListSchema,
  safeJSONStringify,
} from 'shared/helpers/meta';
import { getMetaInfo } from 'shared/layouts/Listing/components/MetaInfo/helpers';
import { isDynamicPageURL } from 'shared/helpers/utils/url';

//selectors
import {
  getLocation,
  getMetaData,
  getIsOfferPage,
  getFirstTwelveProducts,
  getBreadCrumbs,
  getCategoryId,
  getMetaTitle,
} from 'shared/store/listingV2/selectors';
import { getDynamicPageData } from 'shared/store/dynamicPage/selector';

//constants
import { TEXT } from 'shared/layouts/DetailsPage/constants';
import { BRAND_LOGO } from 'shared/constants';

// hook
import { useDynamicPage } from 'shared/routes/DynamicPage/helper';

// context
import { useDomainConfig } from 'shared/domainConfig/context/context';

interface MetaInfo {
  description?: string;
  title?: string;
  keywords?: string[];
  imageUrl?: string;
}

interface MetaProps {
  isSearch: boolean;
}

const Meta = ({ isSearch }: MetaProps) => {
  const metaData = useSelector(getMetaData) as MetaInfo;
  const products = useSelector(getFirstTwelveProducts);
  const location = useSelector(getLocation);
  const { pathname: categoryUrl, search: locationSearch } = location;
  const isOfferPage = useSelector(getIsOfferPage);
  const dynamicPageData = useSelector(getDynamicPageData);
  const breadCrumbs = useSelector(getBreadCrumbs);
  const categoryId = useSelector(getCategoryId);
  const h1Tag = useSelector(getMetaTitle); // H1 for CollectionPage schema as per PRD

  const { slug: dynamicPageSlug } = useDynamicPage();

  const { FAVICON } = useDomainConfig();

  const isDpURL = isDynamicPageURL(categoryUrl);

  const metaInfo = getMetaInfo({
    metaInfo: metaData,
    pathname: categoryUrl,
    categoryURL: categoryUrl,
    isSearch,
    isOffer: isOfferPage,
    dynamicPageSlug,
    dynamicPageData,
    categoryId,
  });

  // As per PRD: Show schemas for PLP, SLP (if indexable), and dynamic pages
  // Do not add SEO schemas for search pages
  const searchParams = new URLSearchParams(locationSearch || '');
  const searchQuery = searchParams.get('q') || searchParams.get('internalSearchTerm') || '';

  // Show schemas only for dynamic pages or non-search pages
  const shouldShowCollectionPage = isDpURL || !isSearch;
  const shouldShowItemList = isDpURL || !isSearch;

  // Generate separate schemas as per PRD requirements for PLP
  // As per PRD: CollectionPage name should be H1 tag of the page
  // For search pages without h1Tag/metaTitle, use search query
  let collectionName = h1Tag || metaInfo.metaTitle;

  if (!collectionName && isSearch && searchQuery) {
    collectionName = `Search results for "${searchQuery}"`;
  }
  // If still no name, don't render CollectionPage (prevents duplicates and invalid schemas)

  // For search pages, use full URL with query params; for category pages use canonical
  const collectionPageUrl =
    isSearch && locationSearch
      ? `${metaInfo.canonicalUrl || ''}${locationSearch}`
      : metaInfo.canonicalUrl || '';

  const collectionPageSchema =
    shouldShowCollectionPage && collectionName
      ? getCollectionPageSchema({
          name: Array.isArray(collectionName) ? collectionName[0] : collectionName,
          description: metaInfo.metaDescription || '',
          url: collectionPageUrl,
        })
      : null;

  // ItemList requires products to exist
  const itemListSchema = shouldShowItemList && products ? getItemListSchema(products, 20) : null;

  // BreadcrumbList also respects shouldShowItemList
  const breadcrumbSchema =
    shouldShowItemList && breadCrumbs.length > 0 ? getBreadcrumbListSchema(breadCrumbs) : null;

  // Build script array for schemas - SAME format as AppContainer (proven to work with SSR)
  const schemaScripts = [];
  if (collectionPageSchema) {
    schemaScripts.push({
      type: 'application/ld+json',
      innerHTML: safeJSONStringify(collectionPageSchema),
    });
  }
  if (itemListSchema) {
    schemaScripts.push({
      type: 'application/ld+json',
      innerHTML: safeJSONStringify(itemListSchema),
    });
  }
  if (breadcrumbSchema) {
    schemaScripts.push({
      type: 'application/ld+json',
      innerHTML: safeJSONStringify(breadcrumbSchema),
    });
  }

  return (
    <Helmet script={schemaScripts}>
      {metaInfo?.metaTitle && <title>{metaInfo.metaTitle}</title>}
      {metaInfo?.metaDescription && <meta name="description" content={metaInfo.metaDescription} />}
      {metaInfo?.metaKeywords && <meta name="keywords" content={metaInfo.metaKeywords} />}

      {metaInfo?.metaTitle && <meta property="og:title" content={metaInfo.metaTitle} />}
      {metaInfo?.metaDescription && (
        <meta property="og:description" content={metaInfo.metaDescription} />
      )}
      <meta property="og:site_name" content={TEXT.GLOBAL_BRAND_NAME} />
      {(metaData?.imageUrl || BRAND_LOGO) && (
        <meta property="og:image" content={metaData?.imageUrl || BRAND_LOGO} />
      )}
      {metaInfo?.metaTitle && <meta name="twitter:title" content={metaInfo.metaTitle} />}
      {metaInfo?.metaDescription && (
        <meta name="twitter:description" content={metaInfo.metaDescription} />
      )}
      <meta name="twitter:card" content="summary_large_image" />
      {metaInfo?.noIndexNoFollowRobotMeta && metaInfo.noIndexNoFollowRobotMeta !== '' && (
        <meta name="robots" content={metaInfo.noIndexNoFollowRobotMeta} />
      )}
      <link rel="icon" type="image/x-icon" href={FAVICON} />
      <link rel="shortcut icon" type="image/x-icon" href={FAVICON} />
      {metaInfo?.canonicalUrl && <link rel="canonical" href={metaInfo.canonicalUrl} />}

      {/* Schemas are now in script prop above - this is the format that works with SSR */}
    </Helmet>
  );
};

export default memo(Meta);
